import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { useSelector } from "react-redux";
import moment from "moment";
import formatNumber, { localeNumber } from "../../hooks/formatNumber";
import { useResizeObserver } from "../widgets/chartWidget/ChartWidget";
import { getFields, findBounds } from "./helpers/graphFields";
import { metricTypes, RANGE_EXTEND, RedArrow, GreenArrow } from "./helpers/constants";
import { useGoalGraphData } from "../../hooks/goalGraphData";

export default function GoalLineDay(props: any) {
	const [initialLoad, setInitialLoad] = useState(true);
	const isLoading = useSelector((state: any) => state.goal.isLoading);
	useEffect(() => {
		if (!isLoading) setInitialLoad(false);
	}, [isLoading]);
	const data = useSelector((state: any) => state.goal.data);
	const graphData = useGoalGraphData(data);
	const { precalculation: apiPrecalculation, precalculationTimeseries } = useSelector((state: any) => state.goal);
	const [precalcPercent, setPrecalcPercent] = useState(0);
	
	const metric = metricTypes[data.metricType]?.metric;
	const fields = getFields(metric, graphData);
	const d3Container = useRef(null);
	const wrapperRef = useRef(null);
	const headerRef = useRef(null);

	const dimensions = useResizeObserver(wrapperRef);
	const headerDimensions = useResizeObserver(headerRef);

	const brandsettings = useSelector((state: any) => state.brandsettings.data);

	const timezone =
		brandsettings && brandsettings.domainSettings && brandsettings.domainSettings.timezone
			? brandsettings.domainSettings.timezone
			: moment.tz.guess();

	const shouldDisplayPrecalcLine = data.chartType === "line" && data.recurrencyType === "daily" && ["pageviews", "videoPageViews", "conversion", "apppageviews", "webpageviews"].includes(data.metricType);

	useEffect(() => {
		if (!dimensions || !headerDimensions || !graphData || graphData.length === 0) return;
		const startDate = new Date(Math.max(+moment().tz(timezone).startOf("day"), +moment(data.iterationStartDate)));
		const endDate = new Date(Math.min(+moment().tz(timezone).endOf("day"), +moment(data.iterationEndDate)));
		const currentData: any = graphData.filter((d: { date: number }) => d.date >= +startDate);
		const yOffset = 240;
		
		let precalculationData = [];

		if (shouldDisplayPrecalcLine) {
			let accPrecalc = 0;
			precalculationData = precalculationTimeseries?.reduce(
				(acc: any[], d: any) => {
					const [hour, minute] = d.date.split(":");
					return [
						...acc,
						{
							date: +moment()
								.set("hour", +hour)
								.set("minute", +minute)
								.set("second", 0)
								.set("millisecond", 0),
							pageviews: ((accPrecalc += d.value) / apiPrecalculation) * data.target,
						},
					];
				},
				currentData.length > 0
				? [{ date: currentData[0].date, pageviews: currentData[0][fields[0]] }]
				: [{ date: Infinity, pageviews: 0 }]
			);
	
			const precalcNow = precalculationData?.reduce(
				(acc: any, d: any) => {
					const absOffset = Math.abs(d.date - +moment());
					if (absOffset < acc.date) {
						return { ...d, date: absOffset };
					} else {
						return acc;
					}
				},
				{ date: Infinity, pageviews: 0 }
			);
	
			const percent = precalculationData ? ((data.achieved - precalcNow.pageviews) / precalcNow.pageviews) * 100 : 0;
			setPrecalcPercent(percent);
		}

		const [valueRangeStart, valueRangeEnd] = findBounds(currentData, fields);
		const rangeStart = Math.min(valueRangeStart, data.target) * (1 - RANGE_EXTEND / 100);
		const rangeEnd = Math.max(valueRangeEnd, data.target) * (1 + RANGE_EXTEND / 100);

		const svg = d3.select(d3Container.current);
		const xScale = d3
			.scaleTime()
			.range([30, dimensions.width - 10])
			.domain([startDate, endDate]);

		const yScale = d3
			.scaleLinear()
			.range([dimensions.height - headerDimensions.height - yOffset, 0])
			.domain([rangeStart, rangeEnd]);

		const xAxis = d3.axisBottom<Date>(xScale).tickFormat((d) => `${d.getHours()}h`);
		const yAxis = d3
			.axisRight(yScale)
			.ticks(10)
			.tickFormat((num) => formatNumber(Number(num)));

		svg.select(".x-axis")
			.style("transform", `translateY(${dimensions.height - headerDimensions.height - yOffset}px)`)
			// @ts-ignore
			.call(xAxis);
		// @ts-ignore
		svg.select(".y-axis").style("transform", "translateX(0px)").call(yAxis);

		svg.selectAll(".grid").remove();

		svg.append("g").attr("class", "grid").call(
			d3
				// @ts-ignore
				.axisLeft()
				// @ts-ignore
				.scale(yScale)
				// @ts-ignore
				.tickSize(-dimensions.width, 0, 0)
				.tickFormat(null)
		);

		svg.selectAll(".goal-now").remove();
		svg.selectAll(".goal-target").remove();
		svg.selectAll(".goal-precalc").remove();
		svg.selectAll(".goal-line").remove();

		svg.append("line")
			.attr("class", "goal-now")
			.attr("x1", xScale(new Date(moment().tz(timezone).format("YYYY-MM-DDTHH:mm:ss"))))
			.attr("x2", xScale(new Date(moment().tz(timezone).format("YYYY-MM-DDTHH:mm:ss"))))
			.attr("y1", yScale(rangeStart))
			.attr("y2", yScale(rangeEnd));

		svg.append("line")
			.attr("class", "goal-target")
			.attr("x1", xScale(startDate))
			.attr("x2", xScale(endDate))
			.attr("y1", yScale(data.target))
			.attr("y2", yScale(data.target));

		const fieldLine = (field: string) =>
			d3
				.line()
				.x((value) => {
					// @ts-ignore
					return xScale(new Date(moment(value.date).tz(timezone).format("YYYY-MM-DDTHH:mm:ss")));
				})
				.y((value) => {
					// @ts-ignore
					return yScale(value[field]);
				})
				.curve(d3.curveCardinal);

		fields.forEach((field) => {
			const line: any = fieldLine(field);
			svg.selectAll("goal-line")
				.data([currentData])
				.join("path")
				.attr("d", line)
				.attr("class", "goal-line")
				.attr("fill", "none")
				.attr("stroke", "white");
		});
		
		if (shouldDisplayPrecalcLine) {
			svg.selectAll(".goal-precalc")
				.data(
					precalculationData
						? [precalculationData.filter((d: any) => d.pageviews > rangeStart && d.date >= startDate)]
						: []
				)
				.join("path")
				.attr("d", fieldLine("pageviews"))
				.attr("class", "goal-precalc")
				.attr("fill", "none")
				.attr("stroke", "white");
		}
	}, [graphData, data, fields, dimensions, headerDimensions, timezone, metric, shouldDisplayPrecalcLine, apiPrecalculation, precalculationTimeseries]);
	
	const graphHTML = initialLoad ? (
		<div>Loading...</div>
	) : (
		<>
			{shouldDisplayPrecalcLine ? (
				<div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
					{isNaN(apiPrecalculation) || apiPrecalculation === null ? (
						<p style={{ color: "grey", lineHeight: "2.5rem" }}>No data to compare available</p>
					) : (
						<h1>
							{precalcPercent >= 0 ? GreenArrow : RedArrow} {Math.abs(precalcPercent).toFixed(2)}%
						</h1>
					)}
				</div>
			) : (
				<div style={{ height: "57px" }}></div>
			)}
			<div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1em" }}>
				<div>Target: {data.target.toLocaleString()}</div>
				<ul className="legend" style={{ textAlign: "right" }}>
					{ shouldDisplayPrecalcLine && <li className="legend-target legend__item">target</li> }
					<li className="legend-progress legend__item">progress</li>
				</ul>
			</div>
			<svg className="d3-component" width={"100%"} ref={d3Container}>
				<g className="x-axis" />
				<g className="y-axis" />
			</svg>
			{!initialLoad && (
				<div
					className="current-progress"
					style={{ textAlign: "center", position: "absolute", bottom: "4rem", width: "100%" }}
				>
					Progress {localeNumber((Number(data.achieved) / Number(data.target)) * 100)}% (
					{data.metricType === 'totalReadingTime' ? (Number(Math.round(data.achieved * 10) / 10).toLocaleString() + ' hrs') : Number(data.achieved).toLocaleString() + ' hits'})
				</div>
			)}
		</>
	);

	return (
		<div className="linechart" ref={wrapperRef}>
			<div className="linechart__heading" ref={headerRef}>
				<div className="linechart__heading-first-row">
					<div style={{ display: "flex", flexDirection: "row", gap: "1rem", cursor: "default" }}>
						<span className="linechart__title">{metricTypes[data.metricType]?.title} Goal</span>
						{data.recurrencyType !== "daily" && <div onClick={props.onCarouselClick}>● ○</div>}
					</div>
					{/* {subtitle && <span className="linechart__subtitle">{subtitle}</span>} */}
					<div className="linechart__heading-right">
						<div style={{ flex: 1 }} />
					</div>
				</div>
				{/* <span className="linechart__extra-info">{extraInfo}</span> */}
			</div>
			{graphHTML}
		</div>
	);
}
